import type { SVGProps } from "react";
import { Button } from "@kalodata/kds-design";
import { IconLineGrowth, IconLineLightning, IconLinePlus, IconSolidAI } from "@kalodata/kds-design-icons";
import { PostVideoButton } from "@components/PostVideoButton/PostVideoButton";
import type { LinkedAccount } from "@store/linkedAccountSlice";
import { useLinkAccount } from "@pages/me/hooks/useLinkAccount";
import { Trans } from "react-i18next";
import { useIsCreditActivityEligible } from "@store/globalSlice";
import { NationalFlag } from "@components/NationalFlag";
import { getRegionIcon } from "@utils/format";
import { useAppSelector } from "@hooks/redux";
import { SOCIAL_PUBLISHER_CREDITS_DAILY_CAP, SOCIAL_PUBLISHER_CREDITS_PER_VIDEO } from "./constants";

const VerifyIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="20" height="20" viewBox="0 0 15 16.0832" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M9.99634 5.72557C10.2404 5.48149 10.6373 5.48149 10.8813 5.72557C11.1248 5.96959 11.125 6.36542 10.8813 6.60936L7.29004 10.2007C6.92846 10.5622 6.33838 10.5503 5.99121 10.175L4.11621 8.14378C3.88208 7.89014 3.89797 7.49412 4.15161 7.25999C4.40517 7.02631 4.80013 7.04215 5.03418 7.29539L6.6626 9.05931L9.99634 5.72557Z"
      fill="var(--kds-accent)"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.02637 0.10545C7.34218 -0.0324358 7.7011 -0.0353269 8.0188 0.0981261C10.6388 1.20035 12.7572 2.01939 14.3726 2.14525C14.7274 2.17284 15 2.44172 15 2.76292V8.64305C15 8.71191 14.9938 8.78155 14.9817 8.84935C14.3656 12.2992 11.9911 14.6927 7.85767 16.0271L7.68066 16.0686C7.56108 16.0874 7.43897 16.0883 7.31934 16.0698L7.14233 16.0271C3.08217 14.7292 0.724544 12.441 0.0720215 9.16185L0.0146484 8.8408L0 8.65281V2.77513C0 2.44935 0.280497 2.17928 0.640869 2.15745C2.10313 2.06907 3.91566 1.42968 6.07788 0.514386L7.02637 0.10545ZM7.52686 1.25169C5.21015 2.26412 3.07048 3.1169 1.25 3.3513V8.64671C1.71469 11.4966 3.6359 13.5876 7.49756 14.8284C11.2978 13.5954 13.2354 11.5056 13.75 8.63572V3.33055C11.9894 3.07468 9.86564 2.2313 7.53418 1.25047L7.52686 1.25169Z"
      fill="var(--kds-accent)"
    />
  </svg>
);

export interface PublishedEmptyStateProps {
  accounts: LinkedAccount[];
  onPostVideo: () => void;
}

export const PublishedEmptyState = ({ accounts, onPostVideo }: PublishedEmptyStateProps) => {
  const linkAccount = useLinkAccount();
  const { isCreditActivityEligible } = useIsCreditActivityEligible();
  const usFlagUrl = useAppSelector((state) => getRegionIcon(state.configReducer.regionOptions, "US"));
  return (
    <div className="pt-[120px] px-[24px]">
      <div className="flex-1 min-w-0 flex flex-col items-center gap-[24px]">
        <div className="flex flex-col items-center gap-[8px]">
          <p className="text-center text-[20px] font-semibold leading-[24px] text-kds-fg">
            <Trans>No Videos Posted Yet</Trans>
          </p>
          {isCreditActivityEligible && (
            <p className="text-center text-[14px] text-kds-fg-soft flex items-center gap-[4px]">
              <Trans
                defaults="<usflag/> US Local and Cross-border Shops earn <coin/> <b>{{points}} Credits per video posted via Kaloclip API</b> (Daily cap: <coin/> <b>{{max}} Credits</b>)"
                values={{
                  points: SOCIAL_PUBLISHER_CREDITS_PER_VIDEO,
                  max: SOCIAL_PUBLISHER_CREDITS_DAILY_CAP,
                }}
                components={{
                  usflag: <NationalFlag url={usFlagUrl} className="h-[14px] w-[18.667px] shrink-0 border-0" />,
                  coin: <IconSolidAI size={16} className="inline shrink-0 text-kds-chart-amber mt-[-6px]" />,
                  b: <span className="font-medium text-kds-fg" />,
                }}
              />
            </p>
          )}
        </div>

        <div className="flex w-full items-center gap-[16px] rounded-[12px] border border-kds-border bg-kds-bg p-[12px]">
          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex items-center gap-[4px]">
              <VerifyIcon className="shrink-0" />
              <span className="text-[14px] font-medium leading-[16px] text-kds-fg">
                <Trans>Official API Authorization</Trans>
              </span>
            </div>
            <p className="h-[32px] line-clamp-2 text-[12px] leading-[16px] text-kds-fg-soft">
              <Trans>Secure publishing with no IP concerns and zero account ban risk</Trans>
            </p>
          </div>

          <div className="h-[56px] w-px shrink-0 bg-kds-border" />

          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex items-center gap-[4px]">
              <IconLineLightning size={20} className="shrink-0 text-kds-chart-violet-solid" />
              <span className="text-[14px] font-medium leading-[16px] text-kds-fg">
                <Trans>Streamlined Operations</Trans>
              </span>
            </div>
            <p className="h-[32px] line-clamp-2 text-[12px] leading-[16px] text-kds-fg-soft">
              <Trans>
                Post to multiple accounts and videos in one click, with scheduled publishing to lock in the best posting
                time
              </Trans>
            </p>
          </div>

          <div className="h-[56px] w-px shrink-0 bg-kds-border" />

          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex items-center gap-[4px]">
              <IconLineGrowth size={20} className="shrink-0 text-kds-success-fg" />
              <span className="text-[14px] font-medium leading-[16px] text-kds-fg">
                <Trans>Official Traffic Boost</Trans>
              </span>
            </div>
            <p className="h-[32px] line-clamp-2 text-[12px] leading-[16px] text-kds-fg-soft">
              <Trans>
                Publishing via the official API gives quality content a chance to receive platform traffic support
              </Trans>
            </p>
          </div>
        </div>

        <div className="flex gap-[12px]">
          {accounts.length === 0 && (
            <Button
              variant="outline"
              size="lg"
              iconPlacement="start"
              icon={<IconLinePlus size={20} />}
              onClick={() => void linkAccount()}
              loading={linkAccount.isPending()}
            >
              <Trans>Connect Account</Trans>
            </Button>
          )}
          <PostVideoButton variant="reverse" onClick={onPostVideo} />
        </div>
      </div>
    </div>
  );
};
